/**
 * Resizable objects (tiled/sliced sprites such as tick-tock and dash platforms, and zones sized by their scale),
 * plus the Unity [ExecuteAlways] sprite generators that keep a resized platform's sprites, collider and colour
 * in sync (TickTockPlatformSpriteGenerator, DashPlatformSpriteGenerator).
 */
import { current, isDraft, original, type Draft } from 'immer';
import type { LevelDoc, PlacedObject } from '../model/format.ts';
import { applyPropertyPath, num } from '../model/unityProps.ts';
import { instantiateObject, refObject } from '../data/prefabInstance.ts';
import { spriteFromRef } from '../data/gameData.ts';
import { setCustomField, setModIn, type ModValue } from './components.ts';
import { prefabInfo } from './catalog.ts';
import { draftObject } from './store.ts';

interface RootComp {
  type: string;
  data: any;
  /** prefab node id (written as an override) or index into the object's own components */
  t?: string;
  index?: number;
}

/** Components on the object's root GameObject, plus the transform node of a named direct child. */
function rootComps(o: PlacedObject): { comps: RootComp[]; childTransform: (name: string) => string | undefined } {
  if (!o.prefab) {
    return { comps: (o.components ?? []).map((c, index) => ({ type: c.type, data: c.data, index })), childTransform: () => undefined };
  }
  const inst = instantiateObject(o);
  if (!inst) return { comps: [], childTransform: () => undefined };
  return {
    comps: inst.root.comps.map((c) => ({ type: c.type, data: c.data, t: c.id })),
    childTransform: (name) => inst.gos.find((g) => g.parent === inst.root && g.name === name)?.transform?.id,
  };
}

function put(o: Draft<PlacedObject>, c: RootComp, path: string, value: ModValue) {
  if (c.t !== undefined) return setModIn(o.mods, c.t, path, value);
  const data = o.components?.[c.index!]?.data;
  if (!data || !value) return;
  if (value.r?.startsWith('asset:')) applyPropertyPath(data, path, '', refObject(value.r));
  else setCustomField(data, path, value);
}

const assetRef = (r: any): string | null => (r?.guid ? `asset:${r.guid}:${r.fileID}` : null);
const sameRef = (a: any, b: any) => assetRef(a) === assetRef(b);
const near = (a: number, b: number) => Math.abs(a - b) < 1e-4;
const sizeOf = (sr: RootComp | undefined): [number, number] => [num(sr?.data.m_Size?.x, 1), num(sr?.data.m_Size?.y, 1)];
const roundHalf = (v: number) => Math.round(v * 2) / 2;

/** Util.UpdateNineThreeOneSprite: which of the four sprite variants a platform of this size uses. */
function nineThreeOne(w: number, h: number): 'single' | 'vertical' | 'horizontal' | 'large' {
  if (near(w, 1) && near(h, 1)) return 'single';
  if (near(w, 1)) return 'vertical';
  if (near(h, 1)) return 'horizontal';
  return 'large';
}

const GENERATORS = ['TickTockPlatformSpriteGenerator', 'DashPlatformSpriteGenerator'];

/**
 * Re-run the sprite generator of an object after its fields changed in this edit (compared with the state
 * before the edit, like the generators compare with the last size they saw).
 */
export function syncGenerators(d: Draft<LevelDoc>, objId: string) {
  const o = draftObject(d, objId);
  if (!o) return;
  const now = rootComps((isDraft(o) ? current(o) : o) as PlacedObject);
  if (now.comps.some((c) => c.type === 'CrumblePlatformGenerator')) return syncCrumble(o, now.comps);
  const gen = now.comps.find((c) => GENERATORS.includes(c.type));
  if (!gen) return;
  const before = isDraft(o) ? rootComps(original(o) as PlacedObject).comps : null;
  const sr = now.comps.find((c) => c.type === 'SpriteRenderer');
  const box = now.comps.find((c) => c.type === 'BoxCollider2D');
  const tick = gen.type === 'TickTockPlatformSpriteGenerator';
  const handler = tick ? now.comps.find((c) => c.type === 'TickTockPlatformHandler') : undefined;
  if (!sr) return;

  const [w0, h0] = sizeOf(sr);
  const old = before ? sizeOf(before.find((c) => c.type === 'SpriteRenderer')) : null;
  if (!old || !near(old[0], w0) || !near(old[1], h0)) {
    const w = tick ? Math.max(0.5, roundHalf(w0)) : w0;
    const h = tick ? Math.max(0.5, roundHalf(h0)) : h0;
    if (!near(w, w0)) put(o, sr, 'm_Size.x', { v: String(w) });
    if (!near(h, h0)) put(o, sr, 'm_Size.y', { v: String(h) });
    const variant = nineThreeOne(w, h);
    const platform = gen.data[tick ? `${variant}SpritePlatform` : `${variant}Sprite`];
    const r = assetRef(platform);
    if (r && !sameRef(sr.data.m_Sprite, platform)) put(o, sr, 'm_Sprite', { r });
    if (handler && r && !sameRef(handler.data.platformSprite, platform)) put(o, handler, 'platformSprite', { r });
    const outline = tick ? gen.data[`${variant}SpriteOutline`] : null;
    const ro = assetRef(outline);
    if (handler && ro && !sameRef(handler.data.outlineSprite, outline)) put(o, handler, 'outlineSprite', { r: ro });
    if (box) {
      const [bw, bh] = sizeOf(box);
      if (!near(bw, w)) put(o, box, 'm_Size.x', { v: String(w) });
      if (!near(bh, h)) put(o, box, 'm_Size.y', { v: String(h) });
    }
    const platformChild = tick ? undefined : now.childTransform('Platform');
    if (platformChild) {
      setModIn(o.mods, platformChild, 'm_LocalScale.x', { v: String(w) });
      setModIn(o.mods, platformChild, 'm_LocalScale.y', { v: String(h) });
    }
  }

  if (handler) {
    const colourKey = (h: RootComp | undefined) => JSON.stringify([h?.data.invert, h?.data.primaryColor, h?.data.invertColor]);
    const prevHandler = before?.find((c) => c.type === 'TickTockPlatformHandler');
    if (!before || colourKey(prevHandler) !== colourKey(handler)) {
      const want = String(handler.data.invert ?? '0') === '1' ? handler.data.invertColor : handler.data.primaryColor;
      if (want) {
        for (const ch of ['r', 'g', 'b', 'a'] as const) {
          if (want[ch] !== undefined && !near(num(sr.data.m_Color?.[ch], 1), num(want[ch], 1))) put(o, sr, `m_Color.${ch}`, { v: String(want[ch]) });
        }
      }
    }
  }
}

/** CrumblePlatformGenerator only accepts whole-tile widths, a height of 1 and x on the half grid. */
function syncCrumble(o: Draft<PlacedObject>, comps: RootComp[]) {
  const sr = comps.find((c) => c.type === 'SpriteRenderer');
  if (!sr) return;
  const [w, h] = sizeOf(sr);
  const rw = Math.max(1, Math.round(w));
  if (!near(rw, w)) put(o, sr, 'm_Size.x', { v: String(rw) });
  if (!near(h, 1)) put(o, sr, 'm_Size.y', { v: '1' });
  const x = roundHalf(o.x);
  if (!near(x, o.x)) o.x = x;
}

const isCrumble = (comps: RootComp[]) => comps.some((c) => c.type === 'CrumblePlatformGenerator');

// ---------- sizing ----------

/** Root tiled/sliced SpriteRenderer of an object (its size is the platform size), if any. */
function sizedSprite(comps: RootComp[]): RootComp | undefined {
  return comps.find((c) => c.type === 'SpriteRenderer' && num(c.data?.m_DrawMode) !== 0);
}

/** Set a tiled sprite's size; a collider that matched the old size follows it. Generators then do the rest. */
export function setSpriteSize(d: Draft<LevelDoc>, id: string, w: number, h: number) {
  const o = draftObject(d, id);
  if (!o) return;
  const { comps } = rootComps((isDraft(o) ? current(o) : o) as PlacedObject);
  const sr = sizedSprite(comps);
  if (!sr) return;
  const box = comps.find((c) => c.type === 'BoxCollider2D');
  const [ow, oh] = sizeOf(sr);
  if (!near(ow, w)) put(o, sr, 'm_Size.x', { v: String(w) });
  if (!near(oh, h)) put(o, sr, 'm_Size.y', { v: String(h) });
  if (box) {
    const [bw, bh] = sizeOf(box);
    if (near(bw, ow) && near(bh, oh)) {
      if (!near(bw, w)) put(o, box, 'm_Size.x', { v: String(w) });
      if (!near(bh, h)) put(o, box, 'm_Size.y', { v: String(h) });
    }
  }
  syncGenerators(d, id);
}

/**
 * How an object is resized. Along each axis its rect spans a*v .. b*v around its position, in the frame of
 * its position and rotation, where v is the sized value (sprite size or |scale|).
 */
export interface ResizeFrame {
  kind: 'sprite' | 'scale';
  x: number;
  y: number;
  rotation: number;
  v: [number, number];
  a: [number, number];
  b: [number, number];
  /** sign of the root scale (scale-sized objects) */
  sign: [number, number];
  /** axes that can be resized */
  axes: [boolean, boolean];
  /** fixed size step and minimum (crumble platforms: whole tiles) */
  step?: number;
  min?: number;
}

export function resizeFrame(o: PlacedObject): ResizeFrame | null {
  const { comps } = rootComps(o);
  const sr = sizedSprite(comps);
  if (sr) {
    const sprite = spriteFromRef(sr.data.m_Sprite);
    const p = sprite?.def.p ?? [0.5, 0.5];
    const [w, h] = sizeOf(sr);
    const k = o.scale;
    const crumble = isCrumble(comps);
    return {
      kind: 'sprite', x: o.x, y: o.y, rotation: o.rotation, v: [w, h],
      a: [-p[0] * k[0], -p[1] * k[1]], b: [(1 - p[0]) * k[0], (1 - p[1]) * k[1]], sign: [1, 1],
      axes: [true, !crumble], ...(crumble ? { step: Math.abs(k[0]) || 1, min: Math.abs(k[0]) || 1 } : {}),
    };
  }
  if (prefabInfo(o.prefab).transform === 'zone') {
    const box = comps.find((c) => c.type === 'BoxCollider2D');
    const [bw, bh] = box ? sizeOf(box) : [1, 1];
    const ox = num(box?.data.m_Offset?.x), oy = num(box?.data.m_Offset?.y);
    const sign: [number, number] = [Math.sign(o.scale[0]) || 1, Math.sign(o.scale[1]) || 1];
    return {
      kind: 'scale', x: o.x, y: o.y, rotation: o.rotation, v: [Math.abs(o.scale[0]), Math.abs(o.scale[1])],
      a: [(ox - bw / 2) * sign[0], (oy - bh / 2) * sign[1]], b: [(ox + bw / 2) * sign[0], (oy + bh / 2) * sign[1]], sign,
      axes: [true, true],
    };
  }
  return null;
}

/** World corners of the frame's rect, counter-clockwise from bottom left. */
export function frameCorners(f: ResizeFrame, off: [number, number] = [0, 0]): [number, number][] {
  const lo = [0, 1].map((i) => Math.min(f.a[i], f.b[i]) * f.v[i]), hi = [0, 1].map((i) => Math.max(f.a[i], f.b[i]) * f.v[i]);
  return ([[lo[0], lo[1]], [hi[0], lo[1]], [hi[0], hi[1]], [lo[0], hi[1]]] as [number, number][]).map(([lx, ly]) => frameToWorld(f, lx, ly, off));
}

export function frameToWorld(f: ResizeFrame, lx: number, ly: number, off: [number, number] = [0, 0]): [number, number] {
  const r = (f.rotation * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  return [f.x + off[0] + c * lx - s * ly, f.y + off[1] + s * lx + c * ly];
}

/** World position of a handle; side is -1/0/1 per axis (0 = middle). */
export function handlePoint(f: ResizeFrame, side: [number, number], off: [number, number] = [0, 0]): [number, number] {
  const l = [0, 1].map((i) => {
    const lo = Math.min(f.a[i], f.b[i]) * f.v[i], hi = Math.max(f.a[i], f.b[i]) * f.v[i];
    return side[i] < 0 ? lo : side[i] > 0 ? hi : (lo + hi) / 2;
  });
  return frameToWorld(f, l[0], l[1], off);
}

const HANDLE_SIDES: [number, number][] = [[-1, -1], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0]];

/** Handles an object offers (only the resizable axes). */
export function handleSides(f: ResizeFrame): [number, number][] {
  return HANDLE_SIDES.filter((s) => (f.axes[0] || !s[0]) && (f.axes[1] || !s[1]));
}

/**
 * New size and position when the handle on `side` is dragged to world point `to`; the opposite edge stays put.
 * Sizes snap to `step` world units (0 = free) and never go below `min`.
 */
export function resizeTo(f: ResizeFrame, side: [number, number], to: [number, number], step: number, min = 0.5): { v: [number, number]; x: number; y: number } {
  const r = (f.rotation * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  const dx = to[0] - f.x, dy = to[1] - f.y;
  const t = [c * dx + s * dy, -s * dx + c * dy];
  const v: [number, number] = [f.v[0], f.v[1]];
  const shift = [0, 0];
  if (f.step) step = f.step;
  if (f.min) min = f.min;
  for (const i of [0, 1]) {
    if (!side[i] || !f.axes[i]) continue;
    const hiC = Math.max(f.a[i], f.b[i]), loC = Math.min(f.a[i], f.b[i]);
    const moving = side[i] > 0 ? hiC : loC, fixed = side[i] > 0 ? loC : hiC;
    const span = Math.abs(moving - fixed) || 1;
    let size = (t[i] - fixed * f.v[i]) * Math.sign(moving - fixed);
    if (step > 0) size = Math.round(size / step) * step;
    size = Math.max(min, size);
    v[i] = size / span;
    shift[i] = fixed * f.v[i] - fixed * v[i];
  }
  return { v, x: f.x + c * shift[0] - s * shift[1], y: f.y + s * shift[0] + c * shift[1] };
}

/** Apply a resize result to the object (inside a change/live recipe). */
export function applyResize(d: Draft<LevelDoc>, id: string, f: ResizeFrame, res: { v: [number, number]; x: number; y: number }) {
  const o = draftObject(d, id);
  if (!o) return;
  o.x = +res.x.toFixed(4);
  o.y = +res.y.toFixed(4);
  if (f.kind === 'sprite') setSpriteSize(d, id, +res.v[0].toFixed(4), +res.v[1].toFixed(4));
  else o.scale = [+(res.v[0] * f.sign[0]).toFixed(4), +(res.v[1] * f.sign[1]).toFixed(4)];
}
